import { BoxProps, Button, Icon, styled } from '@mui/material'
import React from 'react'

import AudioPlayer from '@/app/component/atom/audio/player'
import AudioRecorder, { AudioRecorderRef } from '@/app/component/atom/audio/recorder'
import AudioUploader from '@/app/component/atom/audio/uploader'
import CustomTooltip from '@/app/component/atom/tooltip'
import UploadButton from '@/app/component/atom/upload/upload-button'
import { FileType } from '@/app/types'

import ContentAudioTranscript from './content-audio-transcript'

type Props = {
  audio?: string
  audioFile?: FileType
  downloading: boolean
  enableTTS: boolean
  enableTranscription: boolean
  id: string
  loading: boolean
  playId: string
  recording: boolean
  recorderRef: React.RefObject<AudioRecorderRef>
  transcription: string
  uploading: boolean
  uploadRef: React.RefObject<any>

  onDelete: () => void
  onDownload: () => void
  onPlayToggle: (isPlaying: boolean) => void
  onRecordClick: () => void
  onRecordDisabled: () => void
  onRecordFinish: (file: File) => Promise<void>
  onSelectExisting: () => void
  onStop: () => void
  onTranscriptChange: (transcription: string) => void
  onUpload: (file: File) => Promise<void>
  onUploadStart: (currentFilter: string) => void
}

const ContentAudio: React.FC<Props> = ({
  audio,
  audioFile,
  downloading,
  enableTTS,
  enableTranscription,
  id,
  loading,
  playId,
  recording,
  recorderRef,
  transcription,
  uploading,
  uploadRef,
  onDelete,
  onDownload,
  onPlayToggle,
  onRecordClick,
  onRecordDisabled,
  onRecordFinish,
  onSelectExisting,
  onStop,
  onTranscriptChange,
  onUpload,
  onUploadStart,
}) => {
  const isLoading = loading || downloading || uploading

  return (
    <div data-testid="campaign-content-audio">
      <div>
        <Hidden hidden={!!audio || recording}>
          <AudioUploader
            id="audio-uploader"
            loading={isLoading}
            uploadContent={
              <UploadButton
                selectExisting={onSelectExisting}
                startRecording={onRecordClick}
                startUpload={onUploadStart}
              />
            }
            uploadRef={uploadRef}
            onUpload={onUpload}
          />
        </Hidden>
        <Hidden hidden={!audio || recording}>
          <AudioPlayer
            id={id}
            playId={playId}
            loading={isLoading}
            name={audioFile?.title || audio || ''}
            source={{
              name: audioFile?.title || audio || '',
              url: audioFile?.url || '',
            }}
            onPlayToggle={onPlayToggle}
            onStop={onStop}
          />
        </Hidden>
        <Hidden hidden={!recording}>
          <AudioRecorder
            isRecording={recording}
            ref={recorderRef}
            onDisabled={onRecordDisabled}
            onFinish={onRecordFinish}
          />
        </Hidden>
        {enableTranscription && audioFile && (
          <ContentAudioTranscript
            file={audioFile}
            messageId={id}
            transcription={transcription}
            onChange={onTranscriptChange}
          />
        )}
      </div>
      <ButtonsContainer>
        <ButtonContainer>
          <CustomTooltip title={recording ? 'Finish recording' : 'Record'}>
            <div>
              {!recording && (
                <StyledButton
                  color="primary"
                  data-testid="voice-record-button"
                  disabled={isLoading}
                  variant="outlined"
                  onClick={onRecordClick}
                >
                  <Icon>mic</Icon>
                </StyledButton>
              )}
              {recording && (
                <StyledButton
                  color="primary"
                  data-testid="voice-record-button"
                  disabled={isLoading}
                  variant="outlined"
                  onClick={onRecordClick}
                >
                  <Icon>stop</Icon>
                </StyledButton>
              )}
            </div>
          </CustomTooltip>
        </ButtonContainer>
        <ButtonContainer>
          <CustomTooltip title="Download audio file">
            <div>
              <StyledButton
                className="voice-download-button"
                color="primary"
                data-testid="voice-download-button"
                disabled={isLoading || recording || !audio}
                variant="outlined"
                onClick={onDownload}
              >
                <Icon>cloud_download</Icon>
              </StyledButton>
            </div>
          </CustomTooltip>
        </ButtonContainer>
        <ButtonContainer>
          <CustomTooltip title="Remove audio">
            <div>
              <StyledButton
                className="voice-delete-button"
                color="primary"
                data-testid="voice-delete-button"
                disabled={isLoading || recording || !audio}
                variant="outlined"
                onClick={onDelete}
              >
                <Icon>delete</Icon>
              </StyledButton>
            </div>
          </CustomTooltip>
        </ButtonContainer>
        {!enableTTS && (
          <ButtonContainer>
            <CustomTooltip title="Text to speech - coming soon">
              <div>
                <StyledButton color="primary" variant="outlined" disabled>
                  <Icon>text_fields</Icon>
                </StyledButton>
              </div>
            </CustomTooltip>
          </ButtonContainer>
        )}
      </ButtonsContainer>
    </div>
  )
}

const StyledButton = styled(Button)({
  height: '40px',
  width: '40px',
  borderRadius: '50%',
  minWidth: '0',
})

const ButtonsContainer = styled('div')({
  display: 'flex',
  position: 'relative',
})

const ButtonContainer = styled('div')({
  marginRight: '8px',
})

const Hidden = styled('div')<
  BoxProps & {
    hidden: boolean
  }
>(({ hidden }) => ({
  display: hidden ? 'none' : 'initial',
}))

export default ContentAudio
